import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../services/api';

const LandingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background-color: #000;
  z-index: 1000; /* Cover everything including header if desired, or let header show */
`;

const PosterBackground = styled.div`
  position: absolute;
  top: -5%;
  left: -5%;
  width: 110%;
  height: 110%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
  padding: 20px;
  opacity: 0.4;
  filter: grayscale(0.3) blur(3px);
  z-index: 1;
  pointer-events: none;
`;

const Poster = styled.div`
  aspect-ratio: 2/3;
  background-color: #111;
  border-radius: 12px;
  background-image: ${props => props.src ? `url(https://image.tmdb.org/t/p/w300${props.src})` : 'none'};
  background-size: cover;
  background-position: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.5);
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%);
  z-index: 2;
`;

const Content = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  padding: 60px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(15px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  max-width: 700px;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoBox = styled.div`
  margin-bottom: 2rem;
`;

const AppTitle = styled.h1`
  font-family: 'Carter One', cursive;
  font-size: 5rem;
  margin: 0;
  background: linear-gradient(135deg, #fff 0%, #aaa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 5px 15px rgba(0,0,0,0.5));

  @media (max-width: 600px) {
    font-size: 3.5rem;
  }
`;

const AppTagline = styled.p`
  font-size: 1.4rem;
  margin-bottom: 3rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 300;
  letter-spacing: 0.5px;

  @media (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 25px;
  justify-content: center;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(Link)`
  padding: 18px 45px;
  font-size: 1.2rem;
  font-weight: 700;
  background-color: #fff;
  color: #000;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 180px;

  &:hover {
    transform: translateY(-3px);
    background-color: #f0f0f0;
    box-shadow: 0 10px 20px rgba(255, 255, 255, 0.2);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Link)`
  padding: 18px 45px;
  font-size: 1.2rem;
  font-weight: 700;
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 180px;
  backdrop-filter: blur(5px);

  &:hover {
    transform: translateY(-3px);
    background-color: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const Landing = () => {
    const [posters, setPosters] = useState([]);

    useEffect(() => {
        const fetchPosters = async () => {
            try {
                // Fetch two pages to get enough posters for the grid
                const [p1, p2] = await Promise.all([
                    api.getPopularMovies(1),
                    api.getPopularMovies(2)
                ]);
                const combined = [...p1.results, ...p2.results];
                // Shuffle them a bit
                const shuffled = combined.sort(() => 0.5 - Math.random());
                setPosters(shuffled.slice(0, 40));
            } catch (error) {
                console.error("Failed to fetch landing posters", error);
            }
        };
        fetchPosters();
    }, []);

    return (
        <LandingContainer>
            <PosterBackground>
                {posters.length > 0 ? (
                    posters.map((movie, idx) => (
                        <Poster key={`${movie.id}-${idx}`} src={movie.poster_path} />
                    ))
                ) : (
                    // Fallback placeholders
                    Array(20).fill(0).map((_, idx) => <Poster key={idx} />)
                )}
            </PosterBackground>
            <Overlay />
            <Content>
                <LogoBox>
                    <AppTitle>Kritics</AppTitle>
                </LogoBox>
                <AppTagline>
                    Stop trusting the average score. <br />
                    Discover critics who share your unique movie taste.
                </AppTagline>
                <ButtonGroup>
                    <PrimaryButton to="/register">Join Beta</PrimaryButton>
                    <SecondaryButton to="/login">Sign In</SecondaryButton>
                </ButtonGroup>
            </Content>
        </LandingContainer>
    );
};

export default Landing;
